import Mark from '@/tiptap/tiptap/Utils/Mark';
import { Plugin } from 'prosemirror-state';
import { pasteRule, removeMark, updateMark } from '@/tiptap/commands/commands';
import { safeHref } from '@/lib/url';

// cmd on a mac and ctrl everywhere else, asked of the event rather than of the user agent: a
// browser that spells its platform differently would otherwise send the click to this page
const opensInNewTab = (event) => event.metaKey === true || event.ctrlKey === true;
// the app routes on the hash, and its router only hears the history api: setting a fragment on
// location moves the address bar and nothing else, so an in-app link has to go through the router
export const hashRouteOf = (href) => {
  if (typeof href !== 'string' || href.indexOf(window.location.origin) !== 0) {
    return null;
  }

  const match = /^\/?#(\/.*)$/.exec(href.slice(window.location.origin.length));

  return match === null ? null : match[1];
};

const getTarget = (href, event = { ctrlKey: false, metaKey: false }) => {
  if (typeof href !== 'string') {
    return '_blank';
  }
  if (href.indexOf(window.location.origin) === 0 && !opensInNewTab(event)) {
    return '_self';
  }
  return '_blank';
};

/**
 * A regex that matches any string that contains a link
 */
const pasteRegex = /https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,}\b(?:[-a-zA-Z0-9@:%._+~#=?!&/]*)(?:[-a-zA-Z0-9@:%._+~#=?!&/]*)/gi;

/**
 * A regex that matches an url
 */
const pasteRegexExact = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,}\b(?:[-a-zA-Z0-9@:%._+~#=?!&/]*)(?:[-a-zA-Z0-9@:%._+~#=?!&/]*)$/gi;

export default class Link extends Mark {
  get name() {
    return 'link';
  }

  get defaultOptions() {
    return {
      openOnClick: true,
      linkOnPaste: true,
      target: null,
      router: null,
    };
  }

  get schema() {
    return {
      attrs: {
        href: { default: null },
        target: { default: null },
      },
      inclusive: false,
      parseDOM: [
        {
          tag: 'a[href]',
          getAttrs: (dom) => ({
            href: safeHref(dom.getAttribute('href')),
            target: dom.getAttribute('target'),
          }),
        },
      ],
      toDOM: (node) => {
        // a document stored before the href was checked can still carry an executable scheme
        const href = safeHref(node.attrs.href);

        return ['a', {
          ...node.attrs,
          href,
          rel: 'noopener noreferrer nofollow',
          target: getTarget(href),
        }, 0];
      },
    };
  }

  commands({ type }) {
    return (attrs) => {
      if (attrs.href !== undefined) {
        return updateMark(type, attrs);
      }

      return removeMark(type);
    };
  }

  pasteRules({ type }) {
    return [
      pasteRule(
        pasteRegex,
        type,
        (url) => ({ href: url }),
      ),
    ];
  }

  get plugins() {
    const plugins = [];
    if (this.options.openOnClick) {
      plugins.push(new Plugin({
        props: {
          handleClick: (view, pos, event) => {
            if (!view.editable) {
              return;
            }

            const getMarkAttrs = (state, type) => {
              const node = state.doc.nodeAt(pos);
              if (node === null) {
                return {};
              }

              const mark = node.marks.find((markItem) => markItem.type.name === type.name);

              if (mark === undefined) {
                return {};
              }

              return mark.attrs;
            };

            const { schema } = view.state;
            const attrs = getMarkAttrs(view.state, schema.marks.link);
            const href = safeHref(attrs.href);
            if (href !== undefined && href !== null) {
              const route = hashRouteOf(href);
              switch (getTarget(href, event)) {
                case '_self':
                  if (route !== null && this.options.router !== null) {
                    this.options.router.push(route);
                    return;
                  }
                  window.location.href = href;
                  return;
                case '_blank':
                default:
                  event.stopPropagation();
                  window.open(href, '_blank');
              }
            }
          },
        },
      }));
    }

    if (this.options.linkOnPaste) {
      plugins.push(new Plugin({
        props: {
          handlePaste: (view, event, slice) => {
            const { state } = view;
            const { selection } = state;
            const { empty } = selection;

            if (empty) {
              return false;
            }

            let textContent = '';

            slice.content.forEach((node) => {
              textContent += node.textContent;
            });

            if (!textContent || !textContent.match(pasteRegexExact)) {
              return false;
            }

            this.editor.commands.link({ href: textContent });

            return true;
          },
        },
      }));
    }

    return plugins;
  }
}
